import { CompatibilityRule, CompatibilityTable, CompatibleTableStructure } from './CompatibilityTable.js';
import { Schema } from '../schema.js';
import { ObjectType, TypeKind, TypeReference } from '../types.js';
import { CompatibleValue } from './CompatibleValues.js';
import { TypeService } from '../services/TypeService.js';
import { Model } from '../engine.js';
import { OptionsRegistry } from '../DataSetService/OptionsRegistry.js';


function castString(value: CompatibleValue, ruleIndex: number, index: number): string {
  if (typeof value === 'string') {
    return value as string;
  } else {
    throw new TypeError(`CompatibilityTable rule ${ruleIndex}. Value at '${index}' should be a string`)
  }
}

export class CompatibilityTableFactory {
  private optionIdIndexes: number[];

  constructor(
    public readonly name: string,
    private typeRef: TypeReference,
    private structure: CompatibleTableStructure,
    private rules: CompatibilityRule[],
    private when: (model: Model) => boolean
  ) {
  }

  createCompatibilityTable(schema: Schema, optionsRegistry: OptionsRegistry) {
    this.findOptionIds(schema);
    this.replaceOptionIdWithOption(optionsRegistry);
    const table = new CompatibilityTable(this.structure, schema, this.typeRef);
    table.setWhen(this.when);
    table.addCompatibilityRules(...this.rules);
    return table;
  }

  private replaceOptionIdWithOption(optionsRegistry: OptionsRegistry) {
    for (let optionIdIndex of this.optionIdIndexes) {
      for (let ruleIndex = 0; ruleIndex < this.rules.length; ruleIndex++) {
        const rule = this.rules[ruleIndex];
        const optionId = castString(rule[optionIdIndex], ruleIndex, optionIdIndex);
        const option = optionsRegistry.getOptionById(optionId);
        rule[optionIdIndex] = option;
      }
    }
  }

  private findOptionIds(schema: Schema) {
    this.optionIdIndexes = [];
    for (let columnIndex = 0; columnIndex < this.structure.columns.length; columnIndex++) {
      let columnName = this.structure.columns[columnIndex];
      const { parent, child } = TypeService.getParentChildPath(columnName);

      const typeService = new TypeService(this.typeRef, schema);
      const type = typeService.getPropertyType(parent);
      if (type.kind === TypeKind.Object) {
        const obj = type as ObjectType;
        const childProperty = obj.properties.find(p => p.name === child);
        if (childProperty) {
          if (typeof childProperty.opts.needSource === 'boolean') {
            if (childProperty.opts.needSource) {
              this.optionIdIndexes.push(columnIndex);
            }
          } else {
            this.throwError(`Only property with needSource: true can be used in CompatibilityTable`);
          }
        } else if (!typeService.isPropertyExternal(child)) {
          this.throwError(`Cannot get ${child} for ${parent}`);
        }
      } else {
        this.throwError(`Invalid type of ${type.getTypeReference()}. Object expected`);
      }
    }
  }

  private throwError(message: string) {
    throw new Error(`CompatibilityTable ${this.name}. ${message}`);
  }
}
